/**
 * PlanUpgradeSuccessPage - Success page after plan upgrade payment
 *
 * Shown when a logged-in user completes a plan upgrade via Stripe.
 * Simpler than DeveloperWelcomePage — just confirms the upgrade
 * and provides a "Go to Dashboard" button.
 */

import { PortalLayout } from '@/components/common/PortalLayout';
import { NS } from '@/i18n/namespaces';
import { ECGridColors } from '@/types/colors';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Props
// ============================================================================

export interface PlanUpgradeSuccessPageProps {
  /** The plan the user upgraded to, e.g. "Developer"*/
  targetPlan?: string;
}

// ============================================================================
// Main Component
// ============================================================================

export const PlanUpgradeSuccessPage = React.forwardRef<HTMLDivElement, PlanUpgradeSuccessPageProps>(
  ({ targetPlan }, ref) => {
    const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);

    return (
      <PortalLayout ref={ref} mainClassName="flex items-center justify-center py-8 px-4 sm:px-6">
        <div className="w-full max-w-2xl">
            {/* Success Card */}
            <div className="bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 sm:p-8">
              {/* Success Header */}
              <div className="text-center mb-8">
                {/* Success Icon */}
                <div className="mb-4">
                  <div
                    className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${ECGridColors.blue}15` }}
                  >
                    <CheckCircle2 className="h-8 w-8" style={{ color: ECGridColors.blue }} />
                  </div>
                </div>

                {/* ECGrid Logo */}
                <div className="mb-4">
                  <img
                    src="/img/ecgridlogo2x.png"
                    alt="ECGrid"
                    className="h-12 mx-auto"
                  />
                </div>

                {/* Title */}
                <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: ECGridColors.tealDark }}>
                  {t('PlanUpgradeSuccess_Title', 'Plan Upgrade Successful!')}
                </h1>

                {/* Subtitle */}
                <p className="text-lg sm:text-xl mb-4" style={{ color: ECGridColors.blue }}>
                  {targetPlan
                    ? t('PlanUpgradeSuccess_SubtitleWithPlan', 'Your plan has been upgraded to {{targetPlan}}.', { targetPlan })
                    : t('PlanUpgradeSuccess_Subtitle', 'Your plan has been upgraded successfully.')
                  }
                </p>
              </div>

              {/* Content */}
              <div className="space-y-6 text-gray-700">
                <p className="text-sm sm:text-base leading-relaxed text-center">
                  {t(
                    'PlanUpgradeSuccess_Description',
                    'Your new plan features are now active. You can start using your upgraded capabilities immediately from your dashboard.'
                  )}
                </p>

                {/* Dashboard Button */}
                <div className="my-8 text-center">
                  <a
                    href="/dashboard"
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-lg text-white font-semibold text-base sm:text-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg"
                    style={{ backgroundColor: ECGridColors.blue }}
                    data-testid="go-to-dashboard-btn"
                  >
                    {t('PlanUpgradeSuccess_DashboardButton', 'Go to Dashboard')}
                    <ArrowRight className="h-5 w-5" />
                  </a>
                </div>

                {/* Support Contact */}
                <p className="text-sm sm:text-base leading-relaxed text-center">
                  {t('PlanUpgradeSuccess_Support', 'If you have any questions about your new plan, please contact')}{' '}
                  <a
                    href="mailto:support@ecgrid.com"
                    className="font-medium hover:underline"
                    style={{ color: ECGridColors.blue }}
                  >
                    support@ecgrid.com
                  </a>
                  .
                </p>
              </div>
            </div>
        </div>
      </PortalLayout>
    );
  }
);

PlanUpgradeSuccessPage.displayName = 'PlanUpgradeSuccessPage';
